import { makeAutoObservable, observable, action, computed } from 'mobx'
import type { Assembly } from '@fabrick/store'
import type { Vector3D } from '@fabrick/schema'
import type { SimpleCabinetConfig } from '@fabrick/components'
import { WoodType } from '@fabrick/schema'

/**
 * MobX store for managing PropertiesPanel UI state and property editing actions.
 * Separates property panel logic from the component rendering.
 */
export class PropertiesPanelStore {
  @observable expandedComponentIds = observable.set<string>()
  @observable furnitureStore: Assembly | null = null

  constructor(furnitureStore?: Assembly) {
    this.furnitureStore = furnitureStore || null
    makeAutoObservable(this)
  }

  /**
   * Sets the furniture store reference
   */
  @action
  setFurnitureStore(store: Assembly) {
    this.furnitureStore = store
  }

  /**
   * Toggles the expansion state of a component tree item
   */
  @action
  toggleComponentExpansion(componentId: string) {
    if (this.expandedComponentIds.has(componentId)) {
      // If it's in the set, it's collapsed (remembered as collapsed)
      this.expandedComponentIds.delete(componentId)
    } else {
      // If it's not in the set, add it to mark as collapsed
      this.expandedComponentIds.add(componentId)
    }
  }

  /**
   * Checks if a component is expanded
   * Items start expanded by default (not in the set)
   * Items in the set are collapsed
   */
  isComponentExpanded(componentId: string): boolean {
    return !this.expandedComponentIds.has(componentId)
  }

  /**
   * Sets the expansion state of a component
   */
  @action
  setComponentExpansion(componentId: string, expanded: boolean) {
    if (expanded) {
      // Expanded: remove from set
      this.expandedComponentIds.delete(componentId)
    } else {
      // Collapsed: add to set
      this.expandedComponentIds.add(componentId)
    }
  }

  /**
   * Gets the currently selected component
   */
  @computed
  get selectedComponent() {
    if (!this.furnitureStore) return null
    return this.furnitureStore.selectedComponents[0] || null
  }

  /**
   * Gets root components
   */
  @computed
  get rootComponents() {
    if (!this.furnitureStore) return []
    return this.furnitureStore.rootComponents ?? []
  }

  /**
   * Checks if the selected component is a simple cabinet
   */
  @computed
  get isSimpleCabinet(): boolean {
    return (this.selectedComponent?.metadata?.componentType === 'simple_cabinet') || false
  }

  /**
   * Gets the cabinet config from selected component
   */
  @computed
  get cabinetConfig(): SimpleCabinetConfig | undefined {
    return this.selectedComponent?.metadata?.config as SimpleCabinetConfig | undefined
  }

  /**
   * Gets the resolved cabinet config (with defaults)
   */
  @computed
  get resolvedCabinetConfig(): SimpleCabinetConfig {
    if (this.cabinetConfig) {
      return { ...this.cabinetConfig }
    }
    return {
      width: 0.8,
      height: 1.0,
      depth: 0.5,
      material: WoodType.PLYWOOD,
      doorOpen: 0
    }
  }

  /**
   * Updates cabinet configuration
   */
  @action
  updateCabinetConfig(updates: Partial<SimpleCabinetConfig>) {
    if (!this.furnitureStore || !this.selectedComponent) return
    const nextConfig = { ...this.resolvedCabinetConfig, ...updates }
    if (!this.selectedComponent.metadata) {
      this.selectedComponent.metadata = {}
    }
    this.selectedComponent.setMetadata('config', nextConfig)
  }

  /**
   * Updates cabinet width
   */
  @action
  updateCabinetWidth(width: number) {
    this.updateCabinetConfig({ width })
  }

  /**
   * Updates cabinet height
   */
  @action
  updateCabinetHeight(height: number) {
    this.updateCabinetConfig({ height })
  }

  /**
   * Updates cabinet depth
   */
  @action
  updateCabinetDepth(depth: number) {
    this.updateCabinetConfig({ depth })
  }

  /**
   * Updates cabinet door open angle
   */
  @action
  updateCabinetDoorOpen(doorOpen: number) {
    this.updateCabinetConfig({ doorOpen })
  }

  /**
   * Updates cabinet material
   */
  @action
  updateCabinetMaterial(material: WoodType) {
    this.updateCabinetConfig({ material })
  }

  /**
   * Updates component transform position
   */
  @action
  updateComponentPosition(position: Partial<Vector3D>) {
    if (!this.selectedComponent) return
    const current = this.selectedComponent.layout.transform.position
    this.selectedComponent.setPosition(
      position.x ?? current.x,
      position.y ?? current.y,
      position.z ?? current.z
    )
  }

  /**
   * Updates component position X coordinate
   */
  @action
  updatePositionX(x: number) {
    if (!this.selectedComponent) return
    const pos = this.selectedComponent.layout.transform.position
    this.selectedComponent.setPosition(x, pos.y, pos.z)
  }

  /**
   * Updates component position Y coordinate
   */
  @action
  updatePositionY(y: number) {
    if (!this.selectedComponent) return
    const pos = this.selectedComponent.layout.transform.position
    this.selectedComponent.setPosition(pos.x, y, pos.z)
  }

  /**
   * Updates component position Z coordinate
   */
  @action
  updatePositionZ(z: number) {
    if (!this.selectedComponent) return
    const pos = this.selectedComponent.layout.transform.position
    this.selectedComponent.setPosition(pos.x, pos.y, z)
  }

  /**
   * Updates component transform scale
   */
  @action
  updateComponentScale(scale: Partial<Vector3D>) {
    if (!this.selectedComponent) return
    const current = this.selectedComponent.layout.transform.scale
    this.selectedComponent.setScale(
      scale.x ?? current.x,
      scale.y ?? current.y,
      scale.z ?? current.z
    )
  }

  /**
   * Updates component scale X
   */
  @action
  updateScaleX(x: number) {
    if (!this.selectedComponent) return
    const scale = this.selectedComponent.layout.transform.scale
    this.selectedComponent.setScale(x, scale.y, scale.z)
  }

  /**
   * Updates component scale Y
   */
  @action
  updateScaleY(y: number) {
    if (!this.selectedComponent) return
    const scale = this.selectedComponent.layout.transform.scale
    this.selectedComponent.setScale(scale.x, y, scale.z)
  }

  /**
   * Updates component scale Z
   */
  @action
  updateScaleZ(z: number) {
    if (!this.selectedComponent) return
    const scale = this.selectedComponent.layout.transform.scale
    this.selectedComponent.setScale(scale.x, scale.y, z)
  }

  /**
   * Updates component geometry property
   */
  @action
  updateGeometry(geometry: any) {
    if (!this.selectedComponent || this.selectedComponent.type !== 'panel') return
    const panel = this.selectedComponent as any
    if (panel.setGeometry) {
      panel.setGeometry(geometry)
    }
  }

  /**
   * Updates geometry width
   */
  @action
  updateGeometryWidth(width: number) {
    if (!this.selectedComponent || this.selectedComponent.type !== 'panel') return
    const panel = this.selectedComponent as any
    if (panel.geometry) {
      this.updateGeometry({ ...panel.geometry, width })
    }
  }

  /**
   * Updates geometry height
   */
  @action
  updateGeometryHeight(height: number) {
    if (!this.selectedComponent || this.selectedComponent.type !== 'panel') return
    const panel = this.selectedComponent as any
    if (panel.geometry) {
      this.updateGeometry({ ...panel.geometry, height })
    }
  }

  /**
   * Updates geometry depth/thickness
   */
  @action
  updateGeometryDepth(depth: number) {
    if (!this.selectedComponent || this.selectedComponent.type !== 'panel') return
    const panel = this.selectedComponent as any
    if (panel.geometry) {
      this.updateGeometry({ ...panel.geometry, depth })
    }
  }

  /**
   * Updates component material property
   */
  @action
  updateMaterial(material: any) {
    if (!this.selectedComponent || this.selectedComponent.type !== 'panel') return
    const panel = this.selectedComponent as any
    if (panel.setMaterial) {
      panel.setMaterial(material)
    }
  }

  /**
   * Updates material type
   */
  @action
  updateMaterialType(type: string) {
    if (!this.selectedComponent || this.selectedComponent.type !== 'panel') return
    const panel = this.selectedComponent as any
    if (panel.material) {
      this.updateMaterial({ ...panel.material, type })
    }
  }

  /**
   * Updates material subtype
   */
  @action
  updateMaterialSubtype(subtype: string) {
    if (!this.selectedComponent || this.selectedComponent.type !== 'panel') return
    const panel = this.selectedComponent as any
    if (panel.material) {
      this.updateMaterial({ ...panel.material, subtype })
    }
  }

  /**
   * Updates material color
   */
  @action
  updateMaterialColor(color: string) {
    if (!this.selectedComponent || this.selectedComponent.type !== 'panel') return
    const panel = this.selectedComponent as any
    if (panel.material) {
      this.updateMaterial({ ...panel.material, color })
    }
  }

  /**
   * Toggles component visibility
   */
  @action
  toggleComponentVisibility(componentId: string) {
    if (!this.furnitureStore) return
    const component = this.furnitureStore.components.get(componentId)
    if (!component) return

    // Check if component is currently hidden
    const isHidden = component.layout.hidden ?? false
    component.setVisibility(!isHidden)
  }

  /**
   * Selects a component
   */
  @action
  selectComponent(componentId: string, addToSelection: boolean = false) {
    if (!this.furnitureStore) return
    this.furnitureStore.selectComponent(componentId, addToSelection)
  }

  /**
   * Adds a manufacturing step to the selected component
   */
  @action
  addManufacturingStep(step: any) {
    if (!this.selectedComponent) return
    this.selectedComponent.addManufacturingStep(step)
  }

  /**
   * Removes a manufacturing step from the selected component
   */
  @action
  removeManufacturingStep(stepId: string) {
    if (!this.selectedComponent) return
    this.selectedComponent.removeManufacturingStep(stepId)
  }

  /**
   * Updates a manufacturing step
   */
  @action
  updateManufacturingStep(stepId: string, updates: Partial<any>) {
    if (!this.selectedComponent || !this.selectedComponent.manufacturing_steps) return
    const step = this.selectedComponent.manufacturing_steps.find(s => s.id === stepId)
    if (step) {
      Object.assign(step, updates)
      // Re-sort by order
      this.selectedComponent.manufacturing_steps.sort((a, b) => a.order - b.order)
    }
  }
}
